import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  Input,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  Textarea,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { FaPen, FaPlus, FaTimes } from "react-icons/fa";
import { Redirect } from "react-router-dom";
import { supabase } from "lib/supabase";
import { useSession } from "contexts/SessionContext";

const DOSSIER_VIDE = { titre: "", description: "" };

const STATUTS = {
  collecte: { label: "À générer", color: "orange" },
  matrice_generee: { label: "Matrice générée", color: "green" },
};

export default function CollecteMatrice() {
  const textColor = useColorModeValue("gray.700", "white");
  const toast = useToast();
  const { role, isSuperAdmin } = useSession();
  const formRef = useRef(null);

  const [collectes, setCollectes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [enregistrement, setEnregistrement] = useState(false);

  const [editId, setEditId] = useState(null);
  const [nomVeille, setNomVeille] = useState("");
  const [entreprise, setEntreprise] = useState("");
  const [dossiers, setDossiers] = useState([{ ...DOSSIER_VIDE }]);
  const [concurrents, setConcurrents] = useState([""]);

  const charger = useCallback(async () => {
    setChargement(true);
    const { data, error } = await supabase
      .from("matrice_collectes")
      .select("*")
      .order("created_at", { ascending: false });
    // En cas d'erreur on conserve la liste déjà affichée : un [] la ferait passer
    // pour l'état vide « Aucune collecte » alors que les fiches existent en base.
    if (error) toast({ title: "Chargement des collectes impossible", description: error.message, status: "error", duration: 4000 });
    else setCollectes(data || []);
    setChargement(false);
  }, [toast]);

  useEffect(() => { charger(); }, [charger]);

  function resetForm() {
    setEditId(null);
    setNomVeille("");
    setEntreprise("");
    setDossiers([{ ...DOSSIER_VIDE }]);
    setConcurrents([""]);
  }

  function majDossier(index, champ, valeur) {
    setDossiers((prev) => prev.map((d, i) => (i === index ? { ...d, [champ]: valeur } : d)));
  }

  function retirerDossier(index) {
    setDossiers((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : [{ ...DOSSIER_VIDE }]));
  }

  function majConcurrent(index, valeur) {
    setConcurrents((prev) => prev.map((c, i) => (i === index ? valeur : c)));
  }

  function retirerConcurrent(index) {
    setConcurrents((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : [""]));
  }

  async function enregistrer() {
    const dossiersPropres = dossiers
      .map((d) => ({ titre: d.titre.trim(), description: d.description.trim() }))
      .filter((d) => d.titre);
    const concurrentsPropres = concurrents.map((c) => c.trim()).filter(Boolean);

    if (!nomVeille.trim() || !entreprise.trim()) {
      return toast({ title: "Nom de la veille et entreprise sont requis.", status: "warning", duration: 3000 });
    }
    if (dossiersPropres.length === 0) {
      return toast({ title: "Ajoutez au moins un dossier prioritaire.", status: "warning", duration: 3000 });
    }
    if (dossiers.some((d) => !d.titre.trim() && d.description.trim())) {
      return toast({
        title: "Un dossier a une description mais pas de titre.",
        description: "Donnez-lui un titre ou videz sa description avant d'enregistrer.",
        status: "warning",
        duration: 4000,
      });
    }

    setEnregistrement(true);
    const payload = {
      nom_veille: nomVeille.trim(),
      entreprise: entreprise.trim(),
      dossiers: dossiersPropres,
      concurrents: concurrentsPropres,
    };
    const { error } = editId
      ? await supabase.from("matrice_collectes").update(payload).eq("id", editId)
      : await supabase.from("matrice_collectes").insert(payload);
    setEnregistrement(false);

    if (error) {
      return toast({ title: "Enregistrement impossible", description: error.message, status: "error", duration: 4000 });
    }
    toast({
      title: editId ? "Collecte mise à jour" : "Collecte enregistrée",
      description: `${payload.entreprise} — ${payload.nom_veille}`,
      status: "success",
      duration: 3000,
    });
    resetForm();
    charger();
  }

  function reprendre(c) {
    setEditId(c.id);
    setNomVeille(c.nom_veille || "");
    setEntreprise(c.entreprise || "");
    const ds = Array.isArray(c.dossiers) ? c.dossiers : [];
    setDossiers(ds.length ? ds.map((d) => ({ titre: d.titre || "", description: d.description || "" })) : [{ ...DOSSIER_VIDE }]);
    const cs = Array.isArray(c.concurrents) ? c.concurrents.filter((x) => typeof x === "string") : [];
    setConcurrents(cs.length ? cs : [""]);
    if (formRef.current) formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  const dateCourte = (iso) => new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });

  // Page interne équipe : la route reste montée pour tous (deep-link), on renvoie
  // les rôles non super_admin une fois leur rôle résolu. La RLS protège les données.
  if (role && !isSuperAdmin) return <Redirect to="/admin/dashboard" />;

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Flex align="center" mb="28px" ref={formRef}>
        <Box w="7px" h="60px" borderRadius="full" bg="teal.300" me="18px" flexShrink={0} />
        <Box>
          <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" color={textColor} lineHeight="1.1">Collecte matrice</Text>
        </Box>
      </Flex>

      <Box bg="gray.100" borderRadius="20px" p={{ base: "16px", md: "22px" }} mb="24px">
        <Flex align="center" mb="18px" px="4px">
          <Box w="6px" h="42px" borderRadius="full" bg="teal.300" me="14px" flexShrink={0} />
          <Box>
            <Text fontSize="lg" fontWeight="bold" color={textColor}>
              {editId ? "Modifier une collecte" : "Nouvelle collecte"}
            </Text>
            <Text fontSize="sm" color="gray.700">Renseignez la fiche pendant la session avec le pilote.</Text>
          </Box>
          {editId && (
            <Button ml="auto" size="sm" variant="outline" borderRadius="10px" onClick={resetForm}>
              Annuler la modification
            </Button>
          )}
        </Flex>

        <Box bg="white" borderRadius="16px" p={{ base: "16px", md: "24px" }} boxShadow="0 2px 6px rgba(20,20,43,0.04)">
          <Stack spacing="18px">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing="16px">
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="bold">Nom de la veille</FormLabel>
                <Input value={nomVeille} onChange={(e) => setNomVeille(e.target.value)} placeholder="Veille réglementaire cybersécurité" borderRadius="12px" fontSize="sm" />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="bold">Entreprise</FormLabel>
                <Input value={entreprise} onChange={(e) => setEntreprise(e.target.value)} placeholder="Nom de l'entreprise pilote" borderRadius="12px" fontSize="sm" />
              </FormControl>
            </SimpleGrid>

            <Box>
              <FormLabel fontSize="sm" fontWeight="bold" mb="10px">Dossiers prioritaires</FormLabel>
              <Stack spacing="12px">
                {dossiers.map((d, i) => (
                  <Flex key={i} gap="10px" align="flex-start" bg="gray.50" borderRadius="12px" p="12px">
                    <Flex w="26px" h="26px" borderRadius="8px" bg="teal.50" color="teal.500" align="center" justify="center" fontSize="xs" fontWeight="bold" flexShrink={0} mt="6px">
                      {i + 1}
                    </Flex>
                    <Stack spacing="8px" flex="1">
                      <Input value={d.titre} onChange={(e) => majDossier(i, "titre", e.target.value)} placeholder="Titre du dossier (ex. Réglementation NIS2)" bg="white" borderRadius="10px" fontSize="sm" />
                      <Textarea value={d.description} onChange={(e) => majDossier(i, "description", e.target.value)} placeholder="Description facultative — enjeux, angles, mots-clés évoqués…" bg="white" borderRadius="10px" fontSize="sm" rows={2} resize="vertical" />
                    </Stack>
                    <IconButton aria-label="Retirer ce dossier" icon={<Icon as={FaTimes} />} size="sm" variant="ghost" color="gray.500" mt="6px" onClick={() => retirerDossier(i)} />
                  </Flex>
                ))}
              </Stack>
              <Button mt="12px" size="sm" variant="outline" borderRadius="10px" leftIcon={<Icon as={FaPlus} />} onClick={() => setDossiers((p) => [...p, { ...DOSSIER_VIDE }])}>
                Ajouter un dossier
              </Button>
            </Box>

            <Box>
              <FormLabel fontSize="sm" fontWeight="bold" mb="10px">Concurrents à surveiller</FormLabel>
              <Stack spacing="10px">
                {concurrents.map((c, i) => (
                  <Flex key={i} gap="10px" align="center">
                    <Input value={c} onChange={(e) => majConcurrent(i, e.target.value)} placeholder="Nom du concurrent" borderRadius="10px" fontSize="sm" />
                    <IconButton aria-label="Retirer ce concurrent" icon={<Icon as={FaTimes} />} size="sm" variant="ghost" color="gray.500" onClick={() => retirerConcurrent(i)} />
                  </Flex>
                ))}
              </Stack>
              <Button mt="12px" size="sm" variant="outline" borderRadius="10px" leftIcon={<Icon as={FaPlus} />} onClick={() => setConcurrents((p) => [...p, ""])}>
                Ajouter un concurrent
              </Button>
            </Box>

            <Flex justify="flex-end">
              <Button bg="teal.300" color="white" _hover={{ bg: "teal.400" }} borderRadius="12px" px="28px" isLoading={enregistrement} onClick={enregistrer}>
                {editId ? "Mettre à jour la collecte" : "Enregistrer la collecte"}
              </Button>
            </Flex>
          </Stack>
        </Box>
      </Box>

      <Box bg="gray.100" borderRadius="20px" p={{ base: "16px", md: "22px" }}>
        {chargement ? (
          <Flex justify="center" py="40px"><Spinner color="teal.300" size="lg" /></Flex>
        ) : collectes.length === 0 ? (
          <Box bg="white" borderRadius="16px" p="24px" textAlign="center">
            <Text fontSize="sm" color="gray.500">Aucune collecte pour l'instant — la première fiche apparaîtra ici.</Text>
          </Box>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing="16px">
            {collectes.map((c) => {
              const statut = STATUTS[c.statut] || { label: c.statut, color: "gray" };
              const nbDossiers = Array.isArray(c.dossiers) ? c.dossiers.length : 0;
              const nbConcurrents = Array.isArray(c.concurrents) ? c.concurrents.length : 0;
              const titres = (Array.isArray(c.dossiers) ? c.dossiers : []).map((d) => d.titre).filter(Boolean).join(" · ");
              return (
                <Box key={c.id} bg="white" borderRadius="16px" p="20px" boxShadow="0 2px 6px rgba(20,20,43,0.04)">
                  <Flex align="flex-start" justify="space-between" gap="10px" mb="10px">
                    <Box minW="0">
                      <Text fontSize="md" fontWeight="bold" color="gray.800" isTruncated>{c.entreprise}</Text>
                      <Text fontSize="sm" color="gray.600" isTruncated>{c.nom_veille}</Text>
                    </Box>
                    <Badge colorScheme={statut.color} borderRadius="8px" flexShrink={0}>{statut.label}</Badge>
                  </Flex>
                  <Text fontSize="xs" color="gray.500" noOfLines={2} mb="12px">
                    {titres || "Aucun dossier renseigné"}
                  </Text>
                  <Flex align="center" justify="space-between">
                    <Text fontSize="xs" color="gray.500">
                      {nbDossiers} dossier{nbDossiers > 1 ? "s" : ""} · {nbConcurrents} concurrent{nbConcurrents > 1 ? "s" : ""} · {dateCourte(c.created_at)}
                    </Text>
                    <Button size="xs" variant="outline" borderRadius="8px" leftIcon={<Icon as={FaPen} />} onClick={() => reprendre(c)}>
                      Reprendre
                    </Button>
                  </Flex>
                </Box>
              );
            })}
          </SimpleGrid>
        )}
      </Box>
    </Flex>
  );
}
